import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { gsap } from 'gsap';
import { updateStelle, getNutzerIdentitaet } from './api';
import { profilAnzeigeName } from './utils/profilHelfer';
import { istEntwurf } from './utils/themeUtils';
import { initialen } from './utils/initialen';
import { setzeDragBild } from './utils/dragGhost';
import TextType from './components/TextType';
import Magnet from './components/Magnet';
import { AnimateIcon } from './components/animate-ui/icons/AnimateIcon';
import { Brush } from './components/animate-ui/icons/Brush';
import { Send } from './components/animate-ui/icons/Send';
import { Check } from './components/animate-ui/icons/Check';
import { Pin as AnimatedPin } from './components/animate-ui/icons/Pin';
import './Dashboard.css';

// Überlebt in-App-Navigation, resettet bei echtem Reload
let dashAnimationPlayed = false;

const STATUS_ACTIVITY_CONFIG = {
  '':                      { label: 'Bereit zur Generierung', color: '#d97706', bg: 'rgba(217,119,6,0.12)', borderColor: 'rgba(217,119,6,0.45)' },
  unterlagen_angefertigt:  { label: 'Bereit zum Abschicken', color: '#059669', bg: 'rgba(5,150,105,0.12)', borderColor: 'rgba(5,150,105,0.45)' },
  angefertigt:             { label: 'Bereit zum Abschicken', color: '#059669', bg: 'rgba(5,150,105,0.12)', borderColor: 'rgba(5,150,105,0.45)' },
  abgeschickt:             { label: 'Abgeschickt',        color: '#0284c7', bg: 'rgba(2,132,199,0.12)', borderColor: 'rgba(2,132,199,0.45)' },
  absage:                  { label: 'Absage',             color: '#dc2626', bg: 'rgba(220,38,38,0.12)', borderColor: 'rgba(220,38,38,0.45)' },
  kennenlerngespraech:     { label: 'Interview',          color: '#d97706', bg: 'rgba(217,119,6,0.12)', borderColor: 'rgba(217,119,6,0.45)' },
  zusage:                  { label: 'Zusage!',            color: '#059669', bg: 'rgba(5,150,105,0.12)', borderColor: 'rgba(5,150,105,0.45)' },
};

const NICHT_ABGESCHICKT_STATI = new Set(['', 'unterlagen_angefertigt', 'angefertigt']);

// Basis-Profil: 8 Stammdaten-Felder × 12,5 % = 100 %
export function berechneBasisPct(profile) {
  if (!profile) return 0;
  const felder = ['vorname', 'nachname', 'email', 'telefon', 'strasse', 'hausnummer', 'plz', 'ort'];
  return Math.round(
    felder.filter(k => profile[k] && String(profile[k]).trim().length > 0).length * 12.5
  );
}

function naechsterBasisSchritt(profile) {
  if (!profile?.vorname?.trim())    return 'Trage deinen Vornamen ein.';
  if (!profile?.nachname?.trim())   return 'Ergänze deinen Nachnamen.';
  if (!profile?.email?.trim())      return 'Füge deine E-Mail-Adresse hinzu.';
  if (!profile?.telefon?.trim())    return 'Ergänze deine Telefonnummer.';
  if (!profile?.strasse?.trim())    return 'Gib deine Straße an.';
  if (!profile?.hausnummer?.trim()) return 'Ergänze deine Hausnummer.';
  if (!profile?.plz?.trim())        return 'Trage deine Postleitzahl ein.';
  if (!profile?.ort?.trim())        return 'Gib deinen Wohnort an.';
  return null;
}

export function ProfileCompletionCard({ profile, onClick }) {
  const pct = berechneBasisPct(profile);

  const prevPctRef = useRef(pct);
  const celebratedRef = useRef(!!localStorage.getItem('pcc_celebrated'));

  const [justCompleted, setJustCompleted] = useState(false);

  // Same-session transition: pct wächst auf 100%
  if (!justCompleted && prevPctRef.current < 100 && pct >= 100) {
    setJustCompleted(true);
    localStorage.setItem('pcc_celebrated', '1');
    celebratedRef.current = true;
  }
  prevPctRef.current = pct;

  // Mount mit bereits 100%-Profil, noch nicht gefeiert → Celebration sofort zeigen
  useEffect(() => {
    if (pct >= 100 && !celebratedRef.current) {
      setJustCompleted(true);
      localStorage.setItem('pcc_celebrated', '1');
      celebratedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-Dismiss nach 5s
  useEffect(() => {
    if (!justCompleted) return;
    const t = setTimeout(() => setJustCompleted(false), 9000);
    return () => clearTimeout(t);
  }, [justCompleted]);

  if (pct >= 100 && !justCompleted) return null;

  if (justCompleted) {
    return (
      <div className="profile-completion-card profile-completion-card--done">
        <span className="pcc-checkmark">✓</span>
        <span className="pcc-done-label">Stammdaten vollständig!</span>
        <span className="pcc-done-sub">Deine persönlichen Daten sind eingelesen. Für Bewerbungen füge noch deinen Lebenslauf hinzu.</span>
      </div>
    );
  }

  const abgeschlossen = Math.round(pct / 12.5);
  const naechsterSchritt = naechsterBasisSchritt(profile);

  return (
    <div
      className="profile-completion-card profile-completion-card--active"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      <div className="profile-completion-header">
        <div className="profile-completion-header-left">
          <span className="profile-completion-label">Bewerbungsprofil vervollständigen</span>
          <span className="profile-completion-steps">{abgeschlossen} von 8 Schritten</span>
        </div>
        <span className="profile-completion-pct">{pct}%</span>
      </div>
      <div className="profile-completion-track">
        <div className="profile-completion-fill" style={{ width: `${pct}%` }} />
      </div>
      {naechsterSchritt && (
        <p className="profile-completion-hint">
          <span className="profile-completion-hint-arrow">→</span>
          {naechsterSchritt}
        </p>
      )}
      {onClick && <span className="profile-completion-cta">Jetzt ausfüllen →</span>}
    </div>
  );
}

const GESENDETE_STATI = new Set(['abgeschickt', 'absage', 'kennenlerngespraech', 'zusage']);

function abschickDatum(s) {
  return s.abschick_datum
    ? new Date(s.abschick_datum + 'T00:00:00')
    : (s.erstellt_am ? new Date(s.erstellt_am) : null);
}

function tagLabel(d) {
  const heute = new Date(); heute.setHours(0, 0, 0, 0);
  const tag = new Date(d); tag.setHours(0, 0, 0, 0);
  const diff = Math.round((heute - tag) / 86400000);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Nach Tag gruppieren; Liste kommt bereits absteigend sortiert rein.
function nachTagGruppieren(liste) {
  const gruppen = [];
  liste.forEach((s) => {
    const d = abschickDatum(s);
    const key = d ? d.toISOString().slice(0, 10) : '?';
    let g = gruppen[gruppen.length - 1];
    if (!g || g.key !== key) { g = { key, label: d ? tagLabel(d) : '', items: [] }; gruppen.push(g); }
    g.items.push(s);
  });
  return gruppen;
}

function dieseWoche(stellen) {
  const heute = new Date();
  const monday = new Date(heute);
  monday.setDate(heute.getDate() - ((heute.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const tag = new Date(monday);
    tag.setDate(monday.getDate() + i);
    const von = new Date(tag); von.setHours(0, 0, 0, 0);
    const bis = new Date(tag); bis.setHours(23, 59, 59, 999);
    const anzahl = stellen.filter(s => {
      if (!GESENDETE_STATI.has(s.bewerbungs_status)) return false;
      const d = abschickDatum(s);
      return d && d >= von && d <= bis;
    }).length;
    const wochentag = tag.toLocaleDateString('de-DE', { weekday: 'short' });
    const datum = tag.toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric' });
    return { label: `${datum} ${wochentag}`, anzahl, istHeute: tag.toDateString() === heute.toDateString() };
  });
}

function dieserMonat(stellen) {
  const heute = new Date();
  const j = heute.getFullYear(), m = heute.getMonth();
  const tage = new Date(j, m + 1, 0).getDate();
  return Array.from({ length: tage }, (_, i) => {
    const von = new Date(j, m, i + 1, 0, 0, 0, 0);
    const bis = new Date(j, m, i + 1, 23, 59, 59, 999);
    const anzahl = stellen.filter(s => {
      if (!GESENDETE_STATI.has(s.bewerbungs_status)) return false;
      const d = abschickDatum(s);
      return d && d >= von && d <= bis;
    }).length;
    const istHeute = von.toDateString() === heute.toDateString();
    const tagNr = i + 1;
    return { label: String(tagNr), anzahl, istHeute };
  });
}

const PERIODEN = [
  { key: 'woche', label: '7 Tage',  eyebrow: 'DIESE WOCHE',  hint: 'Reset jeden Montag' },
  { key: 'monat', label: '30 Tage', eyebrow: 'DIESER MONAT', hint: 'Reset am 1. des Monats' },
];

// Abgeschickte Bewerbungen im aktuellen Zeitraum (Woche = ab Montag, Monat = ab dem 1.)
function abgeschicktImZeitraum(stellen, periode) {
  const heute = new Date();
  let von;
  if (periode === 'monat') {
    von = new Date(heute.getFullYear(), heute.getMonth(), 1, 0, 0, 0, 0);
  } else {
    von = new Date(heute);
    von.setDate(heute.getDate() - ((heute.getDay() + 6) % 7));
    von.setHours(0, 0, 0, 0);
  }
  const bis = new Date(heute); bis.setHours(23, 59, 59, 999);
  return stellen
    .filter(s => {
      if (!GESENDETE_STATI.has(s.bewerbungs_status)) return false;
      const d = abschickDatum(s);
      return d && d >= von && d <= bis;
    })
    .sort((a, b) => abschickDatum(b) - abschickDatum(a));
}

function zeitstempel(stelle) {
  if (!stelle) return '';
  if (stelle.erstellt_am) {
    const d = new Date(stelle.erstellt_am);
    const jetzt = new Date();
    const diffMs = jetzt - d;
    const diffH = Math.floor(diffMs / 3600000);
    if (diffH < 1) return 'Gerade eben';
    if (diffH < 24) return `Vor ${diffH} Std.`;
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1) return 'Gestern';
    return `Vor ${diffD} Tagen`;
  }
  return '';
}

// eslint-disable-next-line no-unused-vars
function formatVerfuegbarkeit(wert) {
  if (!wert) return '';
  if (wert === 'ab sofort' || wert === 'flexibel') return wert;
  try {
    const d = new Date(wert + 'T00:00:00');
    return 'ab ' + d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return wert; }
}


function Dashboard({ stellen, nutzer, profile, hasLebenslauf = false, onNeueStelle, onStelleAuswaehlen, onStelleGeaendert, markierteIds = new Set(), onMarkierenToggle, onGenerierenDirekt, onBasisBearbeiten, onLebenslaufHinzufuegen }) {
  const [periode, setPeriode] = useState('woche');
  const [periodePopup, setPeriodePopup] = useState(null);
  const periodePopupTimer = React.useRef(null);
  function wechslePeriode(p) {
    setPeriode(p.key);
    clearTimeout(periodePopupTimer.current);
    setPeriodePopup(null); // unmount first so key-remount fires the animation fresh
    requestAnimationFrame(() => {
      setPeriodePopup({ text: p.hint, id: Date.now() });
      periodePopupTimer.current = setTimeout(() => setPeriodePopup(null), 2600);
    });
  }
  const [identitaet, setIdentitaet] = useState({ vorname: '', nickname: '' });
  useEffect(() => {
    getNutzerIdentitaet().then(res => {
      setIdentitaet({
        vorname: res.data.vorname || '',
        nickname: res.data.nickname || '',
      });
    }).catch(() => {});
  }, []);
  const begruessungsName =
    identitaet.nickname ||
    identitaet.vorname ||
    profile?.vorname ||
    nutzer?.displayName?.split(' ')[0] ||
    'zurück';
  const anzahl = stellen.filter(s => GESENDETE_STATI.has(s.bewerbungs_status || '')).length;
  const gefilterteStellen = stellen.filter(s => !istEntwurf(s));
  const chartDaten = periode === 'monat'
    ? dieserMonat(gefilterteStellen)
    : dieseWoche(gefilterteStellen);
  const maxAnzahl = Math.max(1, ...chartDaten.map((t) => t.anzahl));
  const aktivePeriode = PERIODEN.find(p => p.key === periode);
  const abgeschicktListe = abgeschicktImZeitraum(gefilterteStellen, periode);

  const isFirstVisit = useRef(!dashAnimationPlayed);
  const [typingDone, setTypingDone] = useState(false);
  const [buttonReady, setButtonReady] = useState(false);
  const [lebenslaufHinweisOffen, setLebenslaufHinweisOffen] = useState(false);
  const [hinweisZaehler, setHinweisZaehler] = useState(0);

  const oeffneHinweis = () => {
    const key = !profile ? 'kl_hint_count_no_profile' : 'kl_hint_count_no_lebenslauf';
    const count = parseInt(sessionStorage.getItem(key) || '0') + 1;
    sessionStorage.setItem(key, String(count));
    setHinweisZaehler(count);
    setLebenslaufHinweisOffen(true);
  };

  const [umbenenneId, setUmbenenneId] = useState(null);
  const [umbenenneWert, setUmbenenneWert] = useState('');
  // Abgeschickt-Liste als Fenster beim Klick auf die Grafik
  const [abgeschicktOffen, setAbgeschicktOffen] = useState(false);
  // Pin, über dem die Maus gerade steht — Animation spielt nur beim Hover
  // direkt über der Pinnadel, nicht schon beim Überfahren der ganzen Zeile.
  const [hoverPinId, setHoverPinId] = useState(null);

  useEffect(() => {
    // TYPING_DONE(1.15) + bento delay(0.1) + bento duration(0.85) ≈ 2.1s
    const delayMs = isFirstVisit.current ? 2100 : 350;
    const t = setTimeout(() => setButtonReady(true), delayMs);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 26 chars × 35ms + 200ms initialDelay ≈ 1.1s
    const TYPING_DONE = 1.15;
    if (isFirstVisit.current) {
      dashAnimationPlayed = true;
      // Chart-Card von links reinreiben
      // Stat-Spalte: als Panel von rechts reinreiben, dann alle drei Karten aufploppen
      gsap.set('.bento-chart-card, .stat-card', { opacity: 0, scale: 0.88, transformOrigin: 'center center' });
      gsap.fromTo('.bento-stat-col',
        { opacity: 0, x: 140, rotation: 3 },
        {
          opacity: 1, x: 0, rotation: 0, duration: 0.85, delay: TYPING_DONE + 0.1, ease: 'power3.out',
          onComplete: () => {
            gsap.to('.bento-chart-card, .stat-card', {
              opacity: 1, scale: 1,
              duration: 0.45, stagger: 0.18, ease: 'back.out(1.4)',
            });
          },
        }
      );
      // Aktivitätsliste von unten hochschieben
      gsap.fromTo('.recent-activity',
        { opacity: 0, y: 70 },
        { opacity: 1, y: 0, duration: 0.75, delay: TYPING_DONE + 0.45, ease: 'power3.out' }
      );
      // Profile-Card von oben fallen lassen
      gsap.fromTo('.profile-completion-card',
        { opacity: 0, y: -40 },
        { opacity: 1, y: 0, duration: 0.6, delay: TYPING_DONE, ease: 'power3.out' }
      );
    } else {
      const allSections = ['.dash-header', '.bento-chart-card', '.bento-stat-col', '.recent-activity', '.profile-completion-card'];
      gsap.set(allSections, { opacity: 0, y: 12 });
      gsap.to(allSections, {
        opacity: 1, y: 0,
        duration: 0.38, stagger: 0.09, ease: 'power2.out',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortiert = stellen
    .slice()
    .sort((a, b) => {
      const tDiff = new Date(b.erstellt_am || 0) - new Date(a.erstellt_am || 0);
      if (tDiff !== 0) return tDiff;
      return a.id < b.id ? 1 : -1;
    });

  const nichtAbgeschickt = sortiert.filter(s => (s.text || s.url) && NICHT_ABGESCHICKT_STATI.has(s.bewerbungs_status || ''));
  const recentActivity = nichtAbgeschickt;

  const onAlsAbgeschicktMarkieren = async (e, stelle) => {
    e.stopPropagation();
    try {
      const updates = { bewerbungs_status: 'abgeschickt' };
      if (!stelle.abschick_datum) updates.abschick_datum = new Date().toISOString().slice(0, 10);
      await updateStelle(stelle.id, updates);
      onStelleGeaendert({ ...stelle, ...updates }, 'update');
    } catch {}
  };

  const onMarkieren = (e, stelle) => {
    e.stopPropagation();
    if (onMarkierenToggle) onMarkierenToggle(stelle.id);
  };

  const abgeschicktModal = ReactDOM.createPortal(
    <div className="abg-overlay" onClick={() => setAbgeschicktOffen(false)}>
      <div className="abg-insel" onClick={(e) => e.stopPropagation()}>
        <button className="abg-insel__close" onClick={() => setAbgeschicktOffen(false)}>✕</button>
        <h3>Abgeschickt</h3>
        <p className="abg-hinweis">
          Deine versendeten Bewerbungen {periode === 'monat' ? 'in diesem Monat' : 'in dieser Woche'}.
        </p>
        {abgeschicktListe.length === 0 ? (
          <p className="abg-leer">In diesem Zeitraum noch nichts abgeschickt.</p>
        ) : (
          nachTagGruppieren(abgeschicktListe).map((g) => (
            <div className="abg-gruppe" key={g.key}>
              <div className="abg-gruppe__tag">
                {g.label}<span>{g.items.length}</span>
              </div>
              <ul className="abg-liste">
                {g.items.map((s) => (
                  <li key={s.id} onClick={() => { setAbgeschicktOffen(false); onStelleAuswaehlen(s); }}>
                    <div className="abg-liste__logo">{initialen(s.name)}</div>
                    <div className="abg-liste__info">
                      <strong>{s.name}</strong>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  );

  // eslint-disable-next-line no-unused-vars
  const onUmbenennenStart = (e, stelle) => {
    e.stopPropagation();
    setUmbenenneId(stelle.id);
    setUmbenenneWert(stelle.name);
  };

  const onUmbenennenSpeichern = async (stelle) => {
    if (!umbenenneWert.trim()) { setUmbenenneId(null); return; }
    try {
      await updateStelle(stelle.id, { name: umbenenneWert });
      onStelleGeaendert({ ...stelle, name: umbenenneWert }, 'update');
    } finally {
      setUmbenenneId(null);
    }
  };

  return (
    <div className="dashboard">
      {/* Profile Completion — ganz oben, erst rendern wenn Profil geladen (nicht undefined) */}
      {profile !== undefined && <ProfileCompletionCard profile={profile} onClick={onBasisBearbeiten} />}

      {/* Header */}
      <div className="dash-header">
        <div className="dash-header-text">
          <h1 className="dash-title">
            <div className="dash-title-typing-wrap">
              <span className="dash-title-placeholder">Willkommen zurück,</span>
              {isFirstVisit.current && !typingDone ? (
                <TextType
                  as="span"
                  className="dash-title-typist"
                  text={[`Willkommen zurück,`]}
                  typingSpeed={35}
                  initialDelay={200}
                  loop={false}
                  showCursor={true}
                  cursorCharacter="|"
                  cursorClassName="dash-type-cursor"
                  hideCursorWhileTyping={false}
                  onSentenceComplete={() => setTypingDone(true)}
                />
              ) : (
                <span className="dash-title-typist">Willkommen zurück,</span>
              )}
            </div>
            {(!isFirstVisit.current || typingDone) && (
              <span className="dash-name-accent">{begruessungsName}.</span>
            )}
          </h1>

        </div>
        {(anzahl > 0 || nichtAbgeschickt.length > 0) && buttonReady && (
          <Magnet padding={40} magnetStrength={11}>
            <button
              id="tutorial-neu-btn"
              className="btn-neue-bewerbung"
              onClick={hasLebenslauf ? onNeueStelle : oeffneHinweis}
            >
              Neue Bewerbung<br />starten
            </button>
          </Magnet>
        )}
      </div>

      {/* Bento Grid */}
      <div id="tutorial-bento" className="bento-grid">
        {/* Chart Card */}
        <div className="bento-chart-card bento-chart-card--dark">
          <div className="bento-chart-header">
            <div>
              <div className="bento-chart-eyebrow">
                {periode === 'monat'
                  ? new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }).toUpperCase()
                  : aktivePeriode.eyebrow}
              </div>
              <span className="bento-chart-title">Bewerbungsrate</span>
              {periodePopup && <span key={periodePopup.id} className="bento-chart-hint--popup">{periodePopup.text}</span>}
            </div>
            <div className="bento-chart-header-right">
              <div className="bento-periode-toggle">
                {PERIODEN.map(p => (
                  <button
                    key={p.key}
                    className={`bento-periode-btn${periode === p.key ? ' active' : ''}`}
                    onClick={() => wechslePeriode(p)}
                  >{p.label}</button>
                ))}
              </div>
              <span className="bento-chart-big-num">{chartDaten.reduce((s, t) => s + t.anzahl, 0)}</span>
            </div>
          </div>
          <div
            className="bento-bar-chart bento-bar-chart--clickable"
            onClick={() => setAbgeschicktOffen(o => !o)}
            title="Abgeschickte Bewerbungen anzeigen"
          >
            {chartDaten.map((tag, i) => (
              <div key={i} className="bento-bar-col">
                <span className="bento-bar-count">{tag.anzahl > 0 ? tag.anzahl : ''}</span>
                <div
                  className={`bento-bar${tag.istHeute ? ' bento-bar--accent' : ''}`}
                  style={{ height: `${Math.round((tag.anzahl / maxAnzahl) * 100)}%` }}
                />
                <span className="bento-bar-label">{tag.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stat Cards */}
        <div className="bento-stat-col">
          <div className="stat-card stat-card--primary">
            <strong className="stat-value">{anzahl}</strong>
            <span className="stat-sublabel">Gesamt Bewerbungen</span>
          </div>
          <div className="stat-card">
            <strong className="stat-value">{nichtAbgeschickt.length}</strong>
            <span className="stat-sublabel">Offen</span>
          </div>
        </div>
      </div>

      {abgeschicktOffen && abgeschicktModal}

      {/* Noch nicht Abgeschickt */}
      <div className="recent-activity">
        <div className="section-header">
          <div>
            <h2 className="section-title">Noch nicht Abgeschickt</h2>
          </div>
        </div>

        {nichtAbgeschickt.length === 0 ? (
          <div className="activity-empty">
            {anzahl === 0 ? (
              <>
                <span>Noch keine Bewerbungen. Starte deine erste!</span>
                {buttonReady && (
                  <Magnet padding={40} magnetStrength={11}>
                    <button
                      id="tutorial-neu-btn"
                      className="btn-neue-bewerbung btn-neue-bewerbung--center"
                      onClick={hasLebenslauf ? onNeueStelle : oeffneHinweis}
                    >
                      Neue Bewerbung<br />starten
                    </button>
                  </Magnet>
                )}
              </>
            ) : (
              <span>Alle Bewerbungen sind bereits abgeschickt.</span>
            )}
          </div>
        ) : (
          <div className="activity-list">
          {recentActivity.map((stelle) => (
            <div
              key={stelle.id}
              className="activity-row"
            >
              <div
                className={`activity-item${markierteIds.has(stelle.id) ? ' activity-item--markiert' : ''}${stelle._runningStatus ? ' activity-item--running' : ''}`}
                style={{ borderLeftColor: (STATUS_ACTIVITY_CONFIG[stelle.bewerbungs_status || ''] || STATUS_ACTIVITY_CONFIG['']).borderColor }}
                onClick={() => onStelleAuswaehlen(stelle)}
                draggable
                onDragStart={(e) => { e.dataTransfer.setData('text/plain', stelle.id); e.dataTransfer.effectAllowed = 'move'; setzeDragBild(e); }}
              >
                <div className="activity-logo">
                  {initialen(stelle.name)}
                </div>
                <div className="activity-info">
                  <div className="activity-name-row">
                    {umbenenneId === stelle.id ? (
                      <input
                        className="activity-rename-input"
                        value={umbenenneWert}
                        autoFocus
                        onChange={(e) => setUmbenenneWert(e.target.value)}
                        onBlur={() => onUmbenennenSpeichern(stelle)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') onUmbenennenSpeichern(stelle);
                          if (e.key === 'Escape') setUmbenenneId(null);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <strong className="activity-name">{stelle.name}</strong>
                    )}
                    {stelle._runningStatus ? (
                      <span className="activity-status-badge activity-status-badge--running">
                        Bewerbung wird erstellt
                      </span>
                    ) : (() => {
                      const cfg = STATUS_ACTIVITY_CONFIG[stelle.bewerbungs_status || ''] || STATUS_ACTIVITY_CONFIG[''];
                      return (
                        <span className="activity-status-badge" style={{ color: cfg.color, background: cfg.bg }}>
                          {cfg.label}
                        </span>
                      );
                    })()}
                  </div>
                  <span className="activity-meta">
                    {zeitstempel(stelle)}
                  </span>
                </div>
                <div className={`activity-pin-slot${markierteIds.has(stelle.id) ? ' active' : ''}`}>
                  <button
                    className={`activity-round-btn activity-round-btn--pin${markierteIds.has(stelle.id) ? ' active' : ''}`}
                    title={markierteIds.has(stelle.id) ? 'Markierung entfernen' : 'Markieren'}
                    onClick={(e) => onMarkieren(e, stelle)}
                    onMouseEnter={() => setHoverPinId(stelle.id)}
                    onMouseLeave={() => setHoverPinId(null)}
                  >
                    <AnimatedPin size={22} animate={hoverPinId === stelle.id} style={{ display: 'block' }} />
                  </button>
                </div>
              </div>
              <div className="activity-actions">
                {(stelle.bewerbungs_status === 'unterlagen_angefertigt' || stelle.bewerbungs_status === 'angefertigt') ? (
                  <>
                    <button
                      className="activity-action-btn activity-action-btn--send"
                      title="Bewerbung öffnen und Mappe aufklappen"
                      onClick={(e) => { e.stopPropagation(); onStelleAuswaehlen(stelle, { mappeOffen: true }); }}
                    >
                      <Send size={20} style={{ transform: 'translate(-1px, 1px)' }} />
                      <span className="activity-action-btn__label">Bewerbung abschicken</span>
                    </button>
                    <button
                      className="activity-action-btn activity-action-btn--mark"
                      title="Als Abgeschickt markieren"
                      onClick={(e) => onAlsAbgeschicktMarkieren(e, stelle)}
                    >
                      <Check size={22} />
                      <span className="activity-action-btn__label">Bewerbung bereits abgeschickt</span>
                    </button>
                  </>
                ) : (
                  <AnimateIcon animateOnHover="brush">
                    <button
                      className="activity-action-btn activity-action-btn--generate"
                      title="Bewerbung fortsetzen"
                      onClick={(e) => { e.stopPropagation(); onGenerierenDirekt ? onGenerierenDirekt(stelle) : onStelleAuswaehlen(stelle, { autoAnalyse: true }); }}
                    >
                      <Brush size={22} />
                      <span className="activity-action-btn__label">Bewerbung fortsetzen</span>
                    </button>
                  </AnimateIcon>
                )}
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {lebenslaufHinweisOffen && ReactDOM.createPortal(
        <div className="kein-lebenslauf-overlay" onClick={() => setLebenslaufHinweisOffen(false)}>
          <div className="kein-lebenslauf-insel" onClick={e => e.stopPropagation()}>
            <button className="kein-lebenslauf-insel__close" onClick={() => setLebenslaufHinweisOffen(false)}>✕</button>
            {hinweisZaehler >= 2 ? (
              <>
                <span className="kein-lebenslauf-insel__icon">😄</span>
                <h3>Hey, fehlt immer noch! 😄</h3>
                <p>Wir sehen uns jetzt schon zum {hinweisZaehler}. Mal… In deinem Profil <strong>{!profile ? 'fehlen noch Stammdaten & Lebenslauf' : profilAnzeigeName(profile)}</strong> fehlt der Lebenslauf noch – kurz ergänzen, dann klappt's endlich! 😅</p>
                <button className="kein-lebenslauf-insel__cta" onClick={() => { setLebenslaufHinweisOffen(false); profile ? onLebenslaufHinzufuegen?.() : onBasisBearbeiten(); }}>
                  {!profile ? 'Stammdaten ergänzen →' : 'Lebenslauf hinzufügen →'}
                </button>
              </>
            ) : !profile ? (
              <>
                <span className="kein-lebenslauf-insel__icon">✋</span>
                <h3>Stammdaten & Lebenslauf fehlen noch</h3>
                <p>Trage zuerst deine Stammdaten ein und füge danach deinen Lebenslauf hinzu – dann kann's losgehen!</p>
                <button className="kein-lebenslauf-insel__cta" onClick={() => { setLebenslaufHinweisOffen(false); onBasisBearbeiten(); }}>
                  Stammdaten ergänzen →
                </button>
              </>
            ) : (
              <>
                <span className="kein-lebenslauf-insel__icon">👋</span>
                <h3>Hey, in deinem Profil „{profilAnzeigeName(profile)}" fehlt der Lebenslauf</h3>
                <p>Lade deinen Lebenslauf hoch oder erstelle eigene Sektionen – dann kann's losgehen!</p>
                <button className="kein-lebenslauf-insel__cta" onClick={() => { setLebenslaufHinweisOffen(false); onLebenslaufHinzufuegen?.(); }}>
                  Lebenslauf hinzufügen →
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Dashboard;
